import React, { useState } from 'react';
import { Box, Typography, TextField, Grid, InputAdornment, Button, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import CustomButton from './common/CustomButton';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlineIcon from '@mui/icons-material/EmailOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import SubjectIcon from '@mui/icons-material/Subject';
import MessageIcon from '@mui/icons-material/Message';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const FormSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginTop: '2rem',
  marginBottom: '2rem',
  minHeight: '600px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: '3rem',
  background: '#FFFFFF',
  borderRadius: '20px',
  border: '1px solid rgba(0, 0, 0, 0.12)',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.04)',
  position: 'relative',
  zIndex: 1
}));

const FormTitle = styled(Typography)(({ theme }) => ({
  color: '#1A365D',
  fontSize: '2rem',
  fontWeight: 600,
  marginBottom: '0.5rem',
  position: 'relative'
}));

const FormSubtitle = styled(Typography)(({ theme }) => ({
  color: '#64748B',
  fontSize: '1.1rem',
  marginBottom: '3rem'
}));

const CustomTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    transition: 'all 0.2s ease-in-out',
    '& fieldset': {
      borderColor: '#E2E8F0',
      borderWidth: '1px',
      transition: 'all 0.2s ease-in-out',
    },
    '&:hover': {
      '& fieldset': {
        borderColor: '#94A3B8',
      }
    },
    '&.Mui-focused': {
      '& fieldset': {
        borderColor: '#2563EB',
        borderWidth: '1px'
      }
    }
  },
  '& .MuiInputLabel-root': {
    color: '#64748B',
    fontSize: '0.95rem',
    '&.Mui-focused': {
      color: '#2563EB'
    }
  },
  '& .MuiInputBase-input': {
    padding: '14px',
    fontSize: '0.95rem',
    color: '#1E293B',
  },
  '& .MuiInputAdornment-root': {
    color: '#94A3B8',
    marginRight: '8px',
    '& svg': {
      fontSize: '1.25rem'
    }
  },
  marginBottom: '1.5rem'
}));

const FileInput = styled('input')({
  display: 'none',
});

const FilePreview = styled(Box)(({ theme }) => ({
  marginTop: '1rem',
  padding: '1rem',
  backgroundColor: '#F8FAFC',
  borderRadius: '8px',
  border: '1px solid #E2E8F0'
}));

const CompanySelector = styled(Box)(({ theme }) => ({
  marginBottom: '2rem'
}));

const CompanyOption = styled(Paper)(({ theme, selected }) => ({
  padding: '1.5rem',
  borderRadius: '12px',
  cursor: 'pointer',
  border: '2px solid',
  borderColor: selected ? '#2563EB' : '#E2E8F0',
  backgroundColor: selected ? '#EFF6FF' : '#FFFFFF',
  transition: 'all 0.2s ease-in-out',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: 'none',
  '&:hover': {
    borderColor: selected ? '#2563EB' : '#94A3B8',
    backgroundColor: selected ? '#EFF6FF' : '#F8FAFC',
  }
}));

const CompanyTitle = styled(Typography)(({ theme, selected }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: selected ? '#2563EB' : '#1E293B',
  marginBottom: '0.5rem'
}));

const CompanyDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  color: '#64748B',
  lineHeight: 1.5
}));

const CheckIcon = styled(CheckCircleIcon)(({ theme }) => ({
  position: 'absolute',
  top: '1rem',
  right: '1rem',
  color: '#2563EB',
  fontSize: '1.5rem'
}));

const companies = [
  {
    value: 'air_metrologie',
    label: 'AIR MÉTROLOGIE',
    description: 'Expertise en métrologie et qualité de l\'air'
  },
  {
    value: 'l2mi',
    label: 'L2MI',
    description: 'Solutions innovantes en métrologie industrielle'
  }
];

const ReclamationForm = () => {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleCompanySelect = (companyValue) => {
    setFormData({
      ...formData,
      company: companyValue
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach(key => {
      formDataToSend.append(key, formData[key]);
    });
    if (file) {
      formDataToSend.append('file', file);
    }

    try {
      const response = await fetch('/send-reclamation.php', {
        method: 'POST',
        body: formDataToSend
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus({ type: 'success', message: 'Réclamation envoyée avec succès !' });
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setFile(null);
      } else {
        throw new Error(data.error || 'Une erreur est survenue');
      }
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Erreur lors de l\'envoi de la réclamation. Veuillez réessayer.'
      });
      console.error('Erreur:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormSection>
      <FormContainer>
        <FormTitle variant="h2">
          Soumettre une réclamation
        </FormTitle>
        <FormSubtitle>
          Nous traiterons votre réclamation dans les plus brefs délais
        </FormSubtitle>

        <form onSubmit={handleSubmit}>
          <CompanySelector>
            <Typography
              variant="subtitle1"
              sx={{
                color: '#1E293B',
                fontWeight: 500,
                marginBottom: '1rem'
              }}
            >
              Choisissez l'entreprise concernée :
            </Typography>
            <Grid container spacing={2}>
              {companies.map((company) => (
                <Grid item xs={12} sm={6} key={company.value}>
                  <CompanyOption
                    selected={formData.company === company.value}
                    onClick={() => handleCompanySelect(company.value)}
                  >
                    <CompanyTitle selected={formData.company === company.value}>
                      {company.label}
                    </CompanyTitle>
                    <CompanyDescription>
                      {company.description}
                    </CompanyDescription>
                    {formData.company === company.value && <CheckIcon />}
                  </CompanyOption>
                </Grid>
              ))}
            </Grid>
          </CompanySelector>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                label="Nom complet"
                name="name"
                value={formData.name}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlineIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                label="Téléphone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <CustomTextField
                fullWidth
                label="Sujet de la réclamation"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SubjectIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                fullWidth
                multiline
                rows={4}
                label="Détails de la réclamation"
                name="message"
                value={formData.message}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MessageIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <label htmlFor="file-upload">
                  <FileInput
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    id="file-upload"
                    type="file"
                    onChange={handleFileChange}
                  />
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                    sx={{
                      borderColor: '#E2E8F0',
                      color: '#64748B',
                      '&:hover': {
                        borderColor: '#94A3B8',
                        backgroundColor: '#F8FAFC'
                      }
                    }}
                  >
                    Joindre un fichier
                  </Button>
                </label>
                {file && (
                  <Typography variant="body2" color="textSecondary">
                    {file.name}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <CustomButton
              type="submit"
              disabled={isSubmitting}
              sx={{
                minWidth: '200px',
                height: '48px'
              }}
            >
              {isSubmitting ? 'Envoi en cours...' : 'Envoyer la réclamation'}
            </CustomButton>

            {submitStatus && (
              <Typography
                sx={{
                  mt: 2,
                  color: submitStatus.type === 'success' ? '#10B981' : '#EF4444',
                  fontWeight: 500
                }}
              >
                {submitStatus.message}
              </Typography>
            )}
          </Box>
        </form>
      </FormContainer>
    </FormSection>
  );
};

export default ReclamationForm;
